import { Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { changeOf, paddedDomain } from "../../shared/chartMath";
import { percentage } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared.ts";
import { Quadrant, QUADRANTS, quadrantOf } from "./riskRewardQuadrant";
import {
  REFERENCE_LINE_DASH,
  REFERENCE_LINE_WIDTH,
  ScatterAxis,
  ScatterOverlay,
  ScatterZone,
  toneZoneColors,
  ZoneColor,
  zoneColorOf,
  ZonedScatter,
} from "./scatter/ZonedScatter.tsx";
import { WorstOutcomeChange, worstOutcomeMessage } from "./worstOutcomeMessage";

export interface RiskRewardPoint extends WorstOutcomeChange {
  caseId: string;
  caseName: string;
  // x: adverse exposure removed = actual.worst − litigated.worst (+ = downside cut)
  exposureRemoved: number;
  // y: change in expected net = actual.average − litigated.average (often < 0)
  evChange: number;
}

const toXY = (p: RiskRewardPoint) => ({ x: p.exposureRemoved, y: p.evChange });

// an axis that always holds 0, so both zero lines are on screen
const domainAroundZero = (values: number[]) => paddedDomain([0, ...values]);

/**
 * Risk-vs-reward scatter: one dot per settled case. x = adverse exposure removed
 * by settling (worst-case lift), y = change in expected net result. The two
 * dashed zero lines split the plane into four quadrants: settling that improved
 * both (the favourable tone), the common rational trade — some expected value
 * given up to cut risk (the warning colour), settlements worse on both counts
 * (the adverse tone), and EV gambles that kept more upside at the cost of more
 * exposure (the info colour). Makes "below the EV line" read as a deliberate
 * risk trade rather than a loss. A case on a zero line changed nothing on that
 * axis and takes the colour the other axis gives it; one on both is grey (see
 * quadrantOf).
 */
export const SettlementRiskRewardChart = ({
  title,
  subtitle,
  points,
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  points: RiskRewardPoint[];
  // shown in place of the chart when there is nothing to plot
  emptyMessage: string;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement.riskReward",
  });

  const zones = useMemo<ScatterZone<RiskRewardPoint>[]>(() => {
    const tones = toneZoneColors(theme);
    const colors: { [quadrant in Quadrant]: ZoneColor } = {
      betterBoth: tones.favourable,
      trade: zoneColorOf(theme, theme.palette.warning),
      gamble: zoneColorOf(theme, theme.palette.info),
      dominated: tones.adverse,
      unchanged: tones.neutral,
    };
    return QUADRANTS.map((quadrant) => {
      const inQuadrant = points.filter((p) => quadrantOf(p) === quadrant);
      return {
        key: quadrant,
        points: inQuadrant,
        color: colors[quadrant],
        label: t(quadrant, { count: inQuadrant.length }),
      };
    });
  }, [points, theme, t]);

  const { xAxis, yAxis } = useMemo<{ xAxis: ScatterAxis; yAxis: ScatterAxis }>(
    () => ({
      xAxis: {
        ...domainAroundZero(points.map((p) => p.exposureRemoved)),
        formatTick: currency.formatCompact,
        title: t("xAxis"),
      },
      yAxis: {
        ...domainAroundZero(points.map((p) => p.evChange)),
        formatTick: currency.formatCompact,
        title: t("yAxis"),
      },
    }),
    [points, currency, t],
  );

  // the two dashed zero lines that define the quadrants
  const drawZeroLines: ScatterOverlay = ({ ctx, chartArea, scales }) => {
    if (!scales.x || !scales.y) return;
    const x0 = scales.x.getPixelForValue(0);
    const y0 = scales.y.getPixelForValue(0);
    ctx.save();
    ctx.strokeStyle = theme.palette.text.secondary;
    ctx.lineWidth = REFERENCE_LINE_WIDTH;
    ctx.setLineDash(REFERENCE_LINE_DASH);
    if (x0 >= chartArea.left && x0 <= chartArea.right) {
      ctx.beginPath();
      ctx.moveTo(x0, chartArea.top);
      ctx.lineTo(x0, chartArea.bottom);
      ctx.stroke();
    }
    if (y0 >= chartArea.top && y0 <= chartArea.bottom) {
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y0);
      ctx.lineTo(chartArea.right, y0);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.restore();
  };

  const describeReward = (p: RiskRewardPoint) => {
    switch (changeOf(p.evChange)) {
      case "unchanged":
        return t("evSame");
      case "better":
        return t("evBetter", { amount: currency.format(p.evChange) });
      case "worse":
        return t("evWorse", { amount: currency.format(-p.evChange) });
    }
  };

  const describeRisk = (p: RiskRewardPoint) => {
    const { key, amount, probability } = worstOutcomeMessage(p);
    const sentence = t(key, { amount: currency.format(amount) });
    return probability === undefined
      ? sentence
      : `${sentence}, ${t("trialProbability", {
          probability: percentage.format(probability),
        })}`;
  };

  return (
    <ZonedScatter
      title={title}
      subtitle={subtitle}
      ariaLabel={t("chartLabel")}
      zones={zones}
      toXY={toXY}
      xAxis={xAxis}
      yAxis={yAxis}
      drawOverlay={drawZeroLines}
      emptyMessage={emptyMessage}
      renderTooltip={(point) => (
        <>
          <Typography variant="body2" sx={{ mt: 0.5, ...TABULAR_NUMERALS }}>
            {describeReward(point)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
          >
            {describeRisk(point)}
          </Typography>
        </>
      )}
    />
  );
};
